import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult } from "utils/access";

import { useRunShortcut } from "../../hooks/useRunShortcut";
import { RETRY_RUN } from "./gql";

const CONFIG_REUSE_MESSAGE = "Initial config will be used for retrying the run.";

type RetryRunButtonProps = {
  runId: string;
  stackId: string;
  canRetry: AccessResult;
  isModuleRun: boolean;
  runQueryToRefetch: string;
};

const RetryRunButton = ({
  runId,
  stackId,
  canRetry,
  isModuleRun,
  runQueryToRefetch,
}: RetryRunButtonProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const [retryRun, { loading, data }] = useMutation(RETRY_RUN, {
    variables: { stackId, runId },
    refetchQueries: [runQueryToRefetch],
    awaitRefetchQueries: true,
  });

  const entityUrl = isModuleRun ? "module" : "stack";

  const onClick = useCallback(() => {
    retryRun()
      .then(({ data }) => {
        if (data?.runRetry?.id) {
          const runId = data.runRetry.id;
          reportSuccess({ message: `Retry run ${runId} successfully triggered` });
          navigate(`/${entityUrl}/${stackId}/run/${runId}`);
        }
      })
      .catch(onError);
  }, [retryRun, onError, reportSuccess, navigate, stackId, entityUrl]);

  const isLoading = loading || !!data?.runRetry?.id;
  const tooltipMessage = canRetry.message || CONFIG_REUSE_MESSAGE;

  useRunShortcut({
    label: "Retry",
    code: "mod+alt+r",
    callback: onClick,
    disabled: !canRetry.result,
    loading: isLoading,
  });

  return (
    <Tooltip
      on={(props) => (
        <Button
          {...props}
          disabled={isLoading || !canRetry.result}
          loading={isLoading}
          onPress={onClick}
          variant="primary"
        >
          Retry
        </Button>
      )}
      active
    >
      {tooltipMessage}
    </Tooltip>
  );
};

export default RetryRunButton;
